import { MAX_COMPATIBLE_DEVICES, MAX_DEVICE_TAG_LENGTH, queryKeys } from '@mevaser/shared'
import { Button, Input } from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { api } from '../lib/api.js'

/** Device tags already in use on other products, most used first. */
export function useDeviceSuggestions(): string[] {
  const { data } = useQuery({
    queryKey: queryKeys.products.deviceSuggestions(),
    queryFn: () => api.get<string[]>('/products/device-suggestions'),
    staleTime: 60_000,
  })
  return data ?? []
}

/**
 * The devices a product is compatible with, as one free-text row per device.
 *
 * Rows are held locally (like AttributesEditor) so a half-typed or blank row
 * doesn't vanish mid-edit; what goes up through `onChange` is only the
 * trimmed, non-empty, case-insensitively de-duplicated list. Tags already in
 * use elsewhere are offered as <datalist> suggestions so spellings converge
 * ("iPhone 13", not "iphone13") — a datalist never constrains what's typed.
 */
export function CompatibleDevicesEditor({
  value,
  onChange,
}: {
  value: string[]
  onChange: (next: string[]) => void
}) {
  const nextId = useRef(0)
  const [rows, setRows] = useState<{ id: number; text: string }[]>(() =>
    value.map((text) => ({ id: nextId.current++, text })),
  )
  const listId = useId()
  const suggestions = useDeviceSuggestions()

  function commit(next: { id: number; text: string }[]) {
    setRows(next)
    const seen = new Set<string>()
    const tags: string[] = []
    for (const { text } of next) {
      const tag = text.trim()
      if (tag && !seen.has(tag.toLowerCase())) {
        seen.add(tag.toLowerCase())
        tags.push(tag)
      }
    }
    onChange(tags)
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.id} className="grid grid-cols-[1fr_auto] items-center gap-2">
          <Input
            placeholder="e.g. iPhone 13"
            value={row.text}
            list={listId}
            maxLength={MAX_DEVICE_TAG_LENGTH}
            autoComplete="off"
            onChange={(e) =>
              commit(rows.map((r) => (r.id === row.id ? { ...r, text: e.target.value } : r)))
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove device"
            onClick={() => commit(rows.filter((r) => r.id !== row.id))}
          >
            <X />
          </Button>
        </div>
      ))}
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      {rows.length < MAX_COMPATIBLE_DEVICES ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => commit([...rows, { id: nextId.current++, text: '' }])}
        >
          <Plus /> Add device
        </Button>
      ) : null}
    </div>
  )
}
